/**
 * Sensor metrics configuration for SenCore
 * These are the ONLY metrics currently supported by the backend for storage and retrieval
 */

export interface MetricDefinition {
  userFriendly: string;      // Simple name users can send via API (e.g., "temperature")
  internal: string;           // Internal database field name (e.g., "temperature_celsius")
  unit: string;              // Unit of measurement
  description: string;        // Detailed explanation of what this metric measures
  example: number;           // Example value
  color: string;             // Color for charts and visualizations
}

export const SUPPORTED_METRICS: MetricDefinition[] = [
  {
    userFriendly: 'temperature',
    internal: 'temperature_celsius',
    unit: '°C',
    description: 'Air temperature in degrees Celsius. Measures the ambient temperature of the surrounding air.',
    example: 25.5,
    color: '#1976d2'
  },
  {
    userFriendly: 'humidity',
    internal: 'humidity',  // Note: Backend stores as rh_percentage but API returns as humidity
    unit: '%',
    description: 'Relative humidity percentage (0-100%). Measures the amount of water vapor in the air relative to the maximum amount the air could hold at that temperature. Note: Stored internally as rh_percentage.',
    example: 60.0,
    color: '#2e7d32'
  },
  {
    userFriendly: 'pressure',
    internal: 'atmospheric_pressure_mbar',
    unit: 'mbar',
    description: 'Atmospheric pressure in millibars (mbar). Measures the force exerted by the weight of air in the atmosphere. Standard sea level pressure is approximately 1013.25 mbar.',
    example: 1013.25,
    color: '#7b1fa2'
  },
  {
    userFriendly: 'wind_speed',
    internal: 'wind_speed_m_per_s',
    unit: 'm/s',
    description: 'Wind speed in meters per second (m/s). Measures the horizontal movement of air. Common values range from 0 (calm) to 30+ m/s (storm conditions).',
    example: 5.2,
    color: '#ff6b6b'
  },
  {
    userFriendly: 'wind_direction',
    internal: 'wind_direction_deg',
    unit: '°',
    description: 'Wind direction in degrees (0-360°). Indicates the compass direction from which the wind is blowing. 0° = North, 90° = East, 180° = South, 270° = West.',
    example: 180,
    color: '#f57c00'
  },
  {
    userFriendly: 'rain',
    internal: 'rain_mm',
    unit: 'mm',
    description: 'Rainfall amount in millimeters (mm). Measures the accumulated precipitation. Each mm represents 1 liter of water per square meter.',
    example: 2.5,
    color: '#0288d1'
  },
  {
    userFriendly: 'solar_radiation',
    internal: 'solar_radiation_watt_per_sqm',
    unit: 'W/m²',
    description: 'Solar radiation in watts per square meter (W/m²). Measures the intensity of sunlight reaching the surface. Values range from 0 (night) to ~1000 W/m² (bright sunny day).',
    example: 850,
    color: '#ffa726'
  },
  {
    userFriendly: 'reference_et',
    internal: 'reference_et_mm',
    unit: 'mm',
    description: 'Reference evapotranspiration in millimeters (mm). Represents the amount of water that would evaporate and transpire from a reference grass surface. Used in agriculture and irrigation planning.',
    example: 4.2,
    color: '#00897b'
  }
];

// Helper function to get all user-friendly field names
export const getAllUserFriendlyFields = (): string[] => {
  return SUPPORTED_METRICS.map(m => m.userFriendly);
};

// Helper function to get all internal field names
export const getAllInternalFields = (): string[] => {
  return SUPPORTED_METRICS.map(m => m.internal);
};

// Helper function to get field mapping (user-friendly -> internal)
export const getFieldMapping = (): Record<string, string> => {
  const mapping: Record<string, string> = {};
  SUPPORTED_METRICS.forEach(metric => {
    mapping[metric.userFriendly] = metric.internal;
  });
  return mapping;
};

// Helper to find metric by user-friendly name
export const findMetricByUserFriendly = (name: string): MetricDefinition | undefined => {
  return SUPPORTED_METRICS.find(m => m.userFriendly === name);
};

// Helper to find metric by internal name
export const findMetricByInternal = (name: string): MetricDefinition | undefined => {
  return SUPPORTED_METRICS.find(m => m.internal === name);
};

// Create a metric config object for easy lookup by internal name
export const createMetricConfig = (): Record<string, { name: string; color: string; unit: string; description: string }> => {
  const config: Record<string, { name: string; color: string; unit: string; description: string }> = {};
  SUPPORTED_METRICS.forEach(metric => {
    config[metric.internal] = {
      name: metric.userFriendly.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      color: metric.color,
      unit: metric.unit,
      description: metric.description
    };
  });
  return config;
};
